import moment, { Moment } from "moment";
import {
  OrderTypes,
  PurchaseOrderStatuses,
  shipmentStatuses,
} from "./AutocompleteUtils";
import { VariantProps } from "../Components/Products/AttributesAndVariants/ProductVariants";

/* eslint-disable @typescript-eslint/no-explicit-any */
export class LoginModel {
  username: string | null = "";
  password: string | null = "";
}

export class UserModel {
  _id: string | null = null;
  name: string | null = "";
  email: string | null = "";
  username: string | null = "";
  password: string | null = "";
  confirmPassword: string | null = "";
  role: RoleModel = new RoleModel();
  isEditable = true;
  isActive = true;
}

export interface Task {
  id: number;
  title: string;
  description: string;
  columnId: number;
  priority: string;
  startDate: string;
  endDate: string;
  project: string;
}

export class WorkflowModel {
  _id: string | null = null;
  name: string | null = "";
  description: string | null = "";
  cost = 0;
  time: string | null = "00:00:00";
  statuses: Array<any> = [];
  parentId: string | null = null;
  children: WorkflowModel[] = [];
}

export class WorkflowArrayFromStepModel {
  workflow: string | null = null;
  time: string | null = "00:00:00";
}

export class WorkflowStepModel {
  _id: string | null = null;
  name: string | null = "";
  internalName: string | null = "";
  type: string | null = "";
  addToCheckIn = false;
  workflows: Array<WorkflowArrayFromStepModel> = [];
  defaultValues: Array<any> = [];
}

export class ProductStockModel {
  _id?: string | null = null;
  inventory: InventoryModel | null = null;
  measureUnit? = "";
  quantity = 0;
  totalPrice = 0;
  pricePerQuantity = 0;
  percentageVAT = 0;
  isNew = false;
}
export class ProductModel {
  _id: string | null = null;
  code: string | null = "";
  ean: string | null = "";
  name: string | null = "";
  description: string | null = null;
  type: string | null = "";
  category: string | null = "";
  vat = new TaxRateModel();
  price: string | null = "";
  purchasePrice: string | null = "";
  purchaseTva = 19;
  manufacturer: string | null = "";
  tags: Array<string> = [];
  images: Array<string> = [];
  options: Array<AttributeModel> = [];
  variants: Array<VariantProps> = [];
  files: Array<any> = [];
  inventories: Array<ProductStockModel> = [];
  invoiceId = "";
  hasGuarantee = false;
  guaranteePeriod = 0;
  filteredInventories? = [new InventoryModel()];
}

export class BrandModel {
  _id: string | null = null;
  name: string | null = "";
}

export class ModelModel {
  _id: string | null = null;
  name: string | null = "";
  brand: BrandModel | null = null;
}

export class DeviceModel {
  _id: string | null = null;
  serialNumber: string | null = "";
  imeiNumber1: string | null = "";
  imeiNumber2: string | null = "";
  name: string | null = "";
  configuration: string | null = "";
  code: string | null = "";
  brand: BrandModel | null = null;
  model: ModelModel | null = null;
  purchaseDate: string | Moment | null = moment();
  images: Array<string> = [];
  servicePhotosBefore: Array<string> = [];
  servicePhotosAfter: Array<string> = [];
  location: LocationModel | null = null;
  createdAt?: string | null = "";
}

export class AddressModel {
  city = "";
  county = "";
  country = "";
  details = "";
  postcode = "";
}

export class ContactPerson {
  firstName = "";
  lastName = "";
  email = "";
  phone = "";
}

export class Location {
  address = new AddressModel();
  bank = "";
  iban = "";
  contactPerson = new ContactPerson();
  isWorkingPoint = true;
  notes: NotesModel[] = [];
}

export class TechnicianModel {
  _id?: string | undefined = undefined;
  name = "";
  phone = "";
  email = "";
  partner?: string | PartnerModel | null = null;
}

class UserBaseModel {
  _id?: string | undefined = undefined;
  username = "";
  name = "";
  phone = "";
  email = "";
  companyName = "";
  CNP = "";
  fiscalCode = "";
  category: Array<CustomerTypeModel> = [];
  isLegalPerson = false;
  tradeRegisterNo: string | null = null;
  address: AddressModel = new AddressModel();
  locations: Array<Location> = [new Location()];
  receiveEmails?: boolean = false;
  isMainUser?: boolean = false;
  commercialMarkup?: number;
  cif?: number;
}

export class CustomerModel extends UserBaseModel {
  isVendor = false;
  isPartner = false;
  observations = "";
}

export class VendorModel extends UserBaseModel {
  isVendor = true;
  isPartner = false;
  observations = "";
}

export class PartnerModel extends UserBaseModel {
  isPartner = true;
  isVendor = false;
  observations = "";
  technicians: (string | TechnicianModel)[] = [];
}

export class LocationModel {
  _id?: number | null = null;
  name: string | null = "";
  email: string | null = "";
  phone: string | null = null;
  country: string | null = "";
  address: string | null = "";
  zip: string | null = "";
  state: string | null = null;
  city: string | null = "";
  deliveries: Array<string> = [];
  longitude: string | null = null;
  latitude: string | null = null;
}

export class DeliveryModel {
  _id: string | null = null;
  name: string | null = "";
  shortName: string | null = "";
  description: string | null = null;
}

export class TimeTracking {
  startTime: string | number = 0;
  endTime: string | number | null = null;
  userId: UserModel | null = null;
  isManual = false;
  index?: number | null = null;
}

export class Step {
  stepId: WorkflowStepModel | null = null;
  description: string | null = null;
  images: Array<string> = [];
  files?: Array<any> = [];
  customStepValue: any;
  timeTracking: Array<TimeTracking> = [];
}
export class OrderModel {
  _id?: string | undefined = undefined;
  orderNumber = 0;
  type = OrderTypes.ESTIMATE;
  isClosed = false;
  isFinished = false;
  priority: string | null = "NORMAL";
  notes: Array<NotesModel> = [];
  orderedBy: CustomerModel | null = null;
  handledBy: UserModel | null = null;
  oldStatus: StatusModel | null = null;
  status: StatusModel | null = null;
  location: LocationModel | null = null;
  shipments: Array<any> = [];
  lines: Array<LineModel> = [new LineModel()];
  workflow: WorkflowModel | null = null;
  workflowSteps: Array<Step> = [];
  device: DeviceModel | null = null;
  partner: PartnerModel | null = null;
  partnerTechnician: TechnicianModel | null = null;
  total = 0;
  totalWithoutVat = 0;
  totalCharged = 0;
  totalRemaining = 0;
  createdAt?: string | null = "";
  documents?: Array<string> = [];
  clientPhotos?: Array<string> = [];
  servicePhotosBefore?: Array<string> = [];
  servicePhotosAfter?: Array<string> = [];
  observations?: string = "";
  tags?: Array<string> = [];
}

type LineProductType = {
  _id: string;
  name: string;
  code: string;
  inventory: any;
  variant: any;
  price: number;
};

export class LineModel {
  _id: string | null = null;
  product: LineProductType | null = null;
  inventory: InventoryModel | null = null;
  variant: VariantProps | null = null;
  quantity = 0;
  price = 0;
  vat = 0;
  discount = 0;
  totalWithoutVat = 0;
  total = 0;
}

export class NotesModel {
  _id: string | null = null;
  type: string | null = "";
  title: string | null = "";
  text: string | null = "";
  createdBy: UserModel | null = null;
  attachments: Array<string> = [];
  emailDetails: any = null;
  createdAt?: string;
}

export class AttributeModel {
  _id?: string | null = null;
  name: string | null = "";
  values: Array<string> = [""];
}
export class CategoryModel {
  _id?: string | null = null;
  name: string | null = "";
  description: string | null = "";
}

export class CustomerTypeModel {
  _id?: string | null = null;
  name: string | null = "";
  isPositive = true;
  discountAmmount = 0;
}

export class TypeModel {
  _id?: string | null = null;
  name: string | null = "";
  description: string | null = "";
}

export class PackageModel {
  weight = 1;
  height = 10;
  width = 10;
  label: string | null = null;
  trackingNumber: string | null = null;
  trackingUrl: string | null = null;
  status = shipmentStatuses.pending;
  order: string | null = null;
}

export class ShipmentModel {
  _id: string | undefined = undefined;
  trackingNumber: string | null = "";
  recipient: CustomerModel | null = null;
  senderLocation: LocationModel | null = null;
  status = shipmentStatuses.pending;
  packages: Array<PackageModel> = [new PackageModel()];
  createdAt?: string | null = "";
}

export class StatusModel {
  _id: string | null = null;
  name: string | null = "";
  description: string | null = "";
  workflow: WorkflowModel | null = null;
  isFinalStatus: boolean | null = false;
}

export class RoleModel {
  _id: string | null = null;
  name: string | null = "";
  displayName: string | null = "";
}

export class EmailTemplateModel {
  _id: string | null = null;
  name: string | null = "";
  subject: string | null = "";
  html: any | null = "";
  status: string | null = null;
  cc: string | null = "";
}

export class DocumentTemplateModel {
  _id: string | null = null;
  name: string | null = "";
  filePath: string | null = "";
  type: string | null = "";
  documentInformation: any;
  file: any;
}

export class TemplateStatusModel {
  _id: string | null = null;
  name: string | null = "";
  color = "#FFF";
  sortNumber: number | null = 0;
}

export class InventoryModel {
  _id: string | null = null;
  name: string | null = "";
  description: string | null = "";
  hasRepairItems: boolean | undefined;
  defaultInventory = false;
  location: LocationModel | null = null;
  createdAt: string | null = "";
}

export class StockTransferProductModel {
  product: ProductModel | null = null;
  quantity: number | null = 0;
  totalPrice: number | null = null;
  status = PurchaseOrderStatuses.open;
  measureUnit = new MeasureUnit();
  markup = 0;
  pricePerQuantity: number | null = 0;
  VATvalue = new TaxRateModel();
  maxQuantity?: number | null = 0;
}
export class StockTransferModel {
  _id: string | null = null;
  from: InventoryModel | null = null;
  to: InventoryModel | null = null;
  status: string | null = null;
  products: Array<StockTransferProductModel> = [];
  comments: string | null = "";
  createdAt?: string | null = "";
}

export class PurchaseOrderModel {
  _id: string | null = null;
  purchaseOrderNumber: number | null = 0;
  referenceNumber: string | null = "";
  invoice = new CostModel();
  inventory: InventoryModel | null = null;
  vendor: VendorModel | null = null;
  status = PurchaseOrderStatuses.open;
  products: Array<StockTransferProductModel> = [];
  createdAt?: string | null = "";
  generateNir?: boolean | false;
}

export class LogModel {
  userName = "";
  method = "";
  table = "";
  createdAt: string | null = "";
  updatedAt: string | null = "";
  differences: any;
  identifier: any;
  database = "";
}

export class TaxRateModel {
  _id: string | null = null;
  name: string | null = "";
  value = 0;
  defaultValue = false;
}

export class FiltersModel {
  startDate: Moment = moment().subtract(1, "months");
  endDate: Moment = moment();
  order?: number | string | null = "";
  orderStatus?: any = null;
  user?: any = null;
  customer?: any = null;
  vendor?: any = null;
  device?: any = null;
  workflow?: any = null;
  workflowStatus?: any = null;
  product?: any = null;
  step?: any = null;
  inventory?: any = null;
  type?: any = null;
  category?: any = null;
  stockStatus?: any = null;
  manufacturer?: any = null;
  tags?: any = null;
}

export type ImportExcelDescription = {
  name: string;
  description: string;
  example: string;
};

export class AlertModel {
  _id?: string | undefined = undefined;
  name = "";
  property: string | null = null;
  rule: string | null = null;
  threshold: string | number | null = null;
  color = "#FFF";
  sendEmail = false;
}

export class AlertPerItemsModel {
  _id: string | undefined = undefined;
  modelType = "";
  model: ProductModel = new ProductModel();
  alert: AlertModel = new AlertModel();
}

export class GuaranteeModel {
  _id: string | undefined = undefined;
  model: OrderModel | null = null;
  modelType: string | null = null;
  product: ProductModel | null = null;
  orderDate: Date | null = null;
  guaranteeExpirationDate: Date | null = null;
}

export class PaymentModel {
  currency = "";
  taxValue = 0;
  tax = new TaxRateModel();
  totalWithoutTax = 0;
  totalWithTax = 0;
}

export class InvoiceLine {
  name = "";
  quantity = 0;
  totalWithoutVAT = 0;
  VATvalue = 0;
}

export class CostModel {
  _id: string | undefined = undefined;
  type = "";
  date: Date = new Date();
  description = "";
  payment = new PaymentModel();
  vendor = new VendorModel();
  items: InvoiceLine[] = [];
  documentType = "";
  number: number | undefined = undefined;
  invoiceId = "";
  file = "";
  xmlFileName = "";
}

export class MeasureUnit {
  name = "";
}

export class FgoClientForm {
  name = "";
  uniqueCode = "";
  regCom = "";
  email = "";
  phone = "";
  country = "";
  county = "";
  city = "";
  address = "";
  type: "PF" | "PJ" = "PF";
  externID = 0;
  foreign = false;
  bankAccount = "";
  vatPayer = false;
}

export class FgoContentItemForm {
  name = "";
  articleCode = "";
  stockCode = "";
  description = "";
  unitPrice = 0;
  totalPrice = 0;
  unit = "";
  quantity = 0;
  vatRate = 0;
  costCenterCode = "";
}

export class FgoFormData {
  codUnic = "";
  hash = "";
  text = "";
  explicatii = "";
  valuta = "";
  tipFactura = "";
  dataEmitere = "";
  dataScadenta = "";
  numar = "";
  serie = "";
  tvaLaIncasare = false;
  verificareDuplicat = false;
  valideazaCodUnicRo = false;
  idExtern = "";
  client = new FgoClientForm();
  continut = [new FgoContentItemForm()];
  platformaUrl = "";
}
